import { useState, useMemo, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import SwitchSelector from "react-switch-selector";
import { Scrollbar } from "react-scrollbars-custom";
import Select from "react-select";
import { newSelectStyles } from "../../../common/shared/select.styled";
import { useAccessToken } from "../../../common/hooks/useAccessToken";
import BllinkUtils from "../../../common/utils/utils";
import { calcMonthsTextForSelectBox, shortenText } from "../../../common/shared/statelessMethods";
import { constants } from "../../../common/constants/constants";
import { showLoader, hideLoader } from "../../../store/common/slice";
import { fetchBuildingData } from "../../../store/buildings/actions";
import Search from '../../../images/search.svg';
import { StyledPayments } from "./Payments.styled";
import { PaymentsCard } from "./PaymentsCard";
import { AddPayment } from "./AddPayment";

const StringUtils = BllinkUtils.Strings;
const BllinkTime = BllinkUtils.BllinkTime;

const subTabs = {
    history: 'history',
    addPayment: 'addPayment' 
}

export function Payments({ buildingID, apartmentNumber, setParsedData, setTitleRow, setKeysToIgnore }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();

    const currentBuildingData = useSelector(state => state.buildings.currentBuildingData);
    const ltr = useSelector(state => state.common.ltrEnabled);
    const [rows, setRows] = useState([]);
    const [filteredRows, setFilteredRows] = useState([]);
    const [currentSubTab, setCurrentSubTab] = useState(subTabs.history);
    const [filter, setFilter] = useState('');
    const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
    const [currentPaymentMethod, setCurrentPaymentMethod] = useState();

    const fullData = useMemo(() => rows, [rows]);

    const loadApartmentData = async (apartmentNum) => {
        const response = await client.managers?.reports.getApartmentData(buildingID, apartmentNum) ?? { paymentResults: [], Total: {} };

        if (response.paymentResults) {
            response.paymentResults = response.paymentResults.map((res) => ({
                ...res,
                translatedMethod: res.Method ? t(`buildings.${res.Method}`) : ''
            }));
            setRows(response.paymentResults);
        }
    };

    useEffect(() => {
        let methods = [
            {
                label: t('expenses.all'),
                value: 'all'
            }
        ];
        Object.keys(constants.APIContract.paymentMethodsIds).map(key => {
            methods.push({
                label: t(`buildings.${key}`),
                value: constants.APIContract.paymentMethodsIds[key]
            })
            return methods
        });
        setPaymentMethodOptions(methods);
    }, []);

    useEffect(async () => {
        if (!client) return;
        dispatch(showLoader());

        if (!currentBuildingData.id || currentBuildingData.id !== buildingID) {
            await dispatch(fetchBuildingData({buildingID, options: {urlParams: { skipDisabledCheck: true }}}))
        }

        await loadApartmentData(apartmentNumber);

        dispatch(hideLoader());
    }, [buildingID, apartmentNumber, client]);

    useEffect(() => {
        if (fullData && fullData.length > 0) {
            const keys = ['PaymentDate', 'ClientName', 'Amount', 'CardLast4Digits', 'TenantType', 'paymentFor', 'invoiceDownloadLink', 'Comment', 'Method'];
            const allKeys = Object.keys(fullData[0]);
            const keysToIgnore = allKeys?.filter(item => !keys.includes(item));
            
            const parsedRows = JSON.parse(JSON.stringify(fullData));
            parsedRows.map(row => {
                row.PaymentDate = BllinkTime.parseDate(row.PaymentDate);
                row.Amount = StringUtils.numberWithCommas(row.Amount);
                row.TenantType = t(`reports.${row.TenantType}`) || '';

                const { months, oneTime } = row;
                let text = '';
                if (Array.isArray(months)) text = shortenText(calcMonthsTextForSelectBox(months));
                if (oneTime) text = shortenText(oneTime.description);
                row.paymentFor = text.shortComment || text;
                let comment = shortenText(row.Comment);
                row.Comment = comment?.shortComment || comment || '';
                row.Method = row.translatedMethod;
                return parsedRows
            });

            const filteredRows = parsedRows.map(row => {
                const filteredRow = {};
                for (const key of Object.keys(row)) {
                  if (keys.includes(key)) {
                    filteredRow[key] = row[key];
                  }
                }
                return filteredRow;
            });

            setKeysToIgnore(keysToIgnore);
            setParsedData(filteredRows);
            setFilteredRows(fullData);
            setTitleRow([{
                PaymentDate: t('expenses.date'),
                ClientName: t('reports.tenantName'),
                Amount: t('reports.amount'),
                CardLast4Digits: t('reports.card'),
                TenantType: t('reports.ownership_type'),
                paymentFor: t('reports.for'),
                Comment: t('reports.note'),
                invoiceDownloadLink: t('payments.confirmation_id'),
                Method: t('reports.paymentMethod')
            }]);
        }
    }, [fullData]);

    const onCurrentPaymentMethodChange = e => setCurrentPaymentMethod(e);

    useEffect(() => {
        if (currentPaymentMethod) {
            if (currentPaymentMethod.value === 'all') {
                setFilteredRows(fullData)
            } else {
                let newRows = fullData.slice();
                const filtered = newRows.filter(row => row.paymentMethodId === currentPaymentMethod.value);
                setFilteredRows(filtered);
            }
        }
    }, [currentPaymentMethod]);

    const handlePaymentStop = ({ apartmentNumber }) => loadApartmentData(apartmentNumber);

    return (
        <StyledPayments ltr={ltr}>
            <div className="switch-wrapper">
                <SwitchSelector
                    initialSelectedIndex={1}
                    options={[
                        {
                            label: t('buildings.nav_bar_reports_update_payment'),
                            value: subTabs.addPayment,
                            selectedBackgroundColor: '#E9E9FD',
                        },
                        {
                            label: t('reports.history'),
                            value: subTabs.history,
                            selectedBackgroundColor: '#E9E9FD',
                        }
                    ]}
                    selectedFontColor='#373647'
                    fontColor='rgba(55, 54, 71, 0.7);'
                    backgroundColor='transparent'
                    border='1px solid rgba(110, 116, 255, 0.2)'
                    onChange={e => setCurrentSubTab(e)}
                />
            </div>

            {currentSubTab === subTabs.history && (
                <div className="payments-wrapper">
                    <Scrollbar
                        style={{ width: '100%', height: 'calc(100vh - 380px)' }}
                        trackYProps={{
                            renderer: props => {
                                const { elementRef, ...restProps } = props;
                                return <div {...restProps} ref={elementRef} className="scroll-track" />;
                            }
                        }}
                        thumbYProps={{
                            renderer: props => {
                                const { elementRef, ...restProps } = props;
                                return <div {...restProps} ref={elementRef} className="scroll-thumb" />;
                            }
                        }}
                        rtl={false}
                        noScrollX
                    >
                        <div className="top-bar">
                            <div className="search">
                                <input
                                    type="text"
                                    name="search"
                                    onChange={e => setFilter(e.target.value)}
                                    placeholder={t('search.quick_search')}
                                />
                                <img src={Search} alt={t('search.search')} />
                            </div>

                            <Select
                                className='select'
                                styles={newSelectStyles}
                                options={paymentMethodOptions}
                                value={currentPaymentMethod}
                                onChange={onCurrentPaymentMethodChange}
                                isRtl
                                placeholder={t('payment_settings.method')}
                            />
                        </div> 

                        <div className="payments">
                            <div className="titles">
                                <div>{t('expenses.date')}</div>
                                <div>{t('reports.tenantName')}</div>
                                <div>{t('reports.ownership_type')}</div>
                                <div>{t('reports.amount')}</div>
                                <div>{t('reports.for')}</div>
                                <div>{t('reports.paymentMethod')}</div>
                                <div>{t('reports.note')}</div>
                                <div>{t('payments.confirmation_id')}</div>
                                <div>{t('reports.card')}</div>
                            </div>
                            {filteredRows.map((apt, i) => {
                                return (
                                    <PaymentsCard 
                                        key={i} 
                                        apt={apt} 
                                        filter={filter} 
                                        buildingID={buildingID} 
                                        apartmentNumber={apartmentNumber} 
                                        onPaymentStop={handlePaymentStop}
                                        currency={currentBuildingData?.currency}
                                    />
                                )}
                            )} 
                        </div>               
                    </Scrollbar>
                </div>
            )}

            {currentSubTab === subTabs.addPayment && <AddPayment />}

        </StyledPayments>
    )
}
